import type { FC, ReactNode } from 'react'
import type { TicketTypeView } from '@/types'

import { Minus, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { cms, formatMoney, formatPrice } from '@/lib/format'

import { MAX_TICKETS_PER_ORDER } from '@/constants/platform'

interface TicketSelectorProps {
    ticketTypes: Array<TicketTypeView>
    currency: string
    quantities: Record<string, number>
    onChange: (quantities: Record<string, number>) => void
    disabled?: boolean
}

export const TicketSelector: FC<TicketSelectorProps> = ({ ticketTypes, currency, quantities, onChange, disabled = false }): ReactNode => {
    const totalSelected = Object.values(quantities).reduce((sum, value) => sum + value, 0)

    const setQuantity = (id: string, value: number) => {
        const next = { ...quantities, [id]: Math.max(0, value) }
        if (next[id] === 0) delete next[id]
        onChange(next)
    }

    if (ticketTypes.length === 0) {
        return (
            <p className='rounded-2xl bg-muted p-5 text-sm text-muted-foreground'>
                Tickets are not on sale yet. Check back soon.
            </p>
        )
    }

    return (
        <ul className='flex flex-col gap-3'>
            {ticketTypes.map(type => {
                const selected = quantities[type.id] ?? 0
                const otherSelected = totalSelected - selected
                const max = Math.min(type.maxPerOrder, type.remaining, MAX_TICKETS_PER_ORDER - otherSelected)
                const unavailable = !type.available || disabled
                const fewLeft = type.available && type.remaining <= 10

                return (
                    <li
                        key={type.id}
                        className={cn(
                            'flex flex-col gap-3 rounded-2xl border p-4 transition-colors',
                            selected > 0 ? 'border-primary bg-primary/5' : 'border-border bg-card',
                            unavailable && 'opacity-70'
                        )}
                    >
                        <div className='flex items-start justify-between gap-4'>
                            <div className='flex min-w-0 flex-col gap-1'>
                                <span className='font-semibold' data-mdlfy-cms={cms('ticket_types', type.id, 'name')}>{type.name}</span>

                                {type.description && (
                                    <span className='text-sm leading-snug text-muted-foreground' data-mdlfy-cms={cms('ticket_types', type.id, 'description')}>
                                        {type.description}
                                    </span>
                                )}
                            </div>

                            <span className='shrink-0 font-heading text-lg font-bold'>
                                {formatPrice(type.priceCents, currency)}
                            </span>
                        </div>

                        <div className='flex items-center justify-between gap-4'>
                            <span className={cn('text-xs font-medium', type.unavailableReason ? 'text-destructive' : fewLeft ? 'text-warning' : 'text-muted-foreground')}>
                                {type.unavailableReason
                                    ?? (fewLeft
                                        ? `Only ${type.remaining} left`
                                        : type.salesEndLabel ? `Sales end ${type.salesEndLabel}` : `${type.remaining} available`)}
                            </span>

                            {!unavailable && (
                                <div className='flex items-center gap-1 rounded-full border border-border bg-background p-1'>
                                    <button
                                        type='button'
                                        onClick={() => setQuantity(type.id, selected - 1)}
                                        disabled={selected === 0}
                                        className='flex size-8 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40'
                                        aria-label={`Remove one ${type.name} ticket`}
                                    >
                                        <Minus className='size-4' />
                                    </button>

                                    <span className='w-7 text-center text-sm font-bold tabular-nums' aria-live='polite' aria-label={`${selected} ${type.name} tickets selected`}>
                                        {selected}
                                    </span>

                                    <button
                                        type='button'
                                        onClick={() => setQuantity(type.id, selected + 1)}
                                        disabled={selected >= max}
                                        className='flex size-8 cursor-pointer items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40'
                                        aria-label={`Add one ${type.name} ticket`}
                                    >
                                        <Plus className='size-4' />
                                    </button>
                                </div>
                            )}
                        </div>
                    </li>
                )
            })}
        </ul>
    )
}

export const summarizeSelection = (ticketTypes: Array<TicketTypeView>, quantities: Record<string, number>) => {
    const lines = ticketTypes
        .filter(type => (quantities[type.id] ?? 0) > 0)
        .map(type => ({
            id: type.id,
            name: type.name,
            quantity: quantities[type.id],
            unitPrice: type.priceCents,
            total: type.priceCents * quantities[type.id]
        }))

    const count = lines.reduce((sum, line) => sum + line.quantity, 0)
    const paidCount = lines.filter(line => line.unitPrice > 0).reduce((sum, line) => sum + line.quantity, 0)
    const subtotal = lines.reduce((sum, line) => sum + line.total, 0)

    return { lines, count, paidCount, subtotal }
}

export const formatTotal = (cents: number, currency: string): string => {
    return cents === 0 ? 'Free' : formatMoney(cents, currency)
}
